import Api from '@/Api'

export default class EventsApi extends Api {
  /**
   * Attempts to authenticate a user given a set of credentials
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   * @param {String}          options.body.password - the user's password
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  userAuth = (options = {}) => {
    return this.post('/api/v1/public/users/authentication', options.body, options.params, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * Fetches payment history for the group/subgroup in context with additional supported filters
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   * @param {Object}   options.body - Location information to be created
   */
  createLocation = (options = {}) => {
    return this.post(`/api/v1/private/parent-groups/events/location/create`, options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * This endpoint updates details of an event location.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   * @param {string}   options.groupEventLocationId
   * @param {Object}   options.body - Location information to be created
   */
  updateEventLocation = (options = {}) => {
    return this.put(`/api/v1/private/parent-groups/events/location`, options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * This endpoint deletes a location that is not currently attached to a pending event. Delete should not return an error when deleting an event that does not exist or has already been deleted, but it will return an error if you're not authorized to modify a given parent group. Also, attempting to delete an event that doesn't belong to a parent group will not return an error, it just will not modify the event.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   * @param {string}   options.groupEventLocationId
   */
  deleteEventLocation = (options = {}) => {
    return this.delete(`/api/v1/private/parent-groups/events/location`, options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * This endpoint retrieves details of a parent group’s currently scheduled events.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   */
  getEventRequestList = (options = {}) => {
    return this.post(`/api/v1/private/parent-groups/events/event-list `,options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * This endpoint creates a pending event request.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   * @param {Object}   options - Event Request information to be created
   */
  createEventRequest = (options = {}) => {
    return this.post(`/api/v1/private/parent-groups/events/create`, options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * This endpoint cancels an event that is currently pending.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   * @param {String}   options.groupEventLocationId
   */
  cancelEventRequest = (options = {}) => {
    return this.delete(`/api/v1/private/parent-groups/events`, options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * Returns a distinct list of benefits for a given Parent Group
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   */
  getParentGroupProductKeys = (options = {}) => {
    return this.post(`/api/v1/private/parent-groups/product-keys`, options,{}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * Returns a distinct list of benefits for a given Parent Group
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   * @param {String}   options.groupCk - Group context
   * @param {String}   options.classId - Class Id
   * @param {String}   options.classPlanId - Class Plan Id
   * @param {String}   options.dateOfService = Date of Service
   */
  getParentGroupBenefits = (options = {}) => {
    return this.post(`/api/v1/private/parent-groups/benefits`, options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * This endpoint retrieves an event location.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   * @param {String}   options.groupEventLocationId - Group location Id
   */
  getLocationEvent = (options = {}) => {
    return this.post('/api/v1/private/parent-groups/events/location', options, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * To return group details based on name,id, or paraent group indicator.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.groupId - Group Id
   * @param {String}   options.groupName - Group name
   * @param {Boolean}  options.isParentGroupSearch - Group name
   */
  getGroupSearch = (options = {}) => {
    // for some reason, all the url encoding stuff which happens was being 
    //   bypassed here.  
    let payload = {
      id: options.groupId,
      name: options.groupName,
      isParentGroupSearch: options.isParentGroupSearch
    }
    return this.post(`/api/v1/private/groups`, payload, {}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * To return group details based on name,id, or paraent group indicator.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - parentGroupCk
   */
  getParentGroupSettings = (options = {}) => {
    return this.post(`/api/v1/private/parent-groups/events/settings`, options,{}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * To return group details based on name,id, or paraent group indicator.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - parentGroupCk
   */
  updateParentGroupSettings = (options = {}) => {
    return this.put(`/api/v1/private/parent-groups/events/settings`, options,{}, process.env.VUE_APP_BETA_MODE)
  }

  /**
   * This endpoint retrieves event locations.
   * @param {Object}   [options={}] - configuration for the API request
   * @param {String}   options.parentGroupCk - Parent group context
   */
  getEventLocationsList = (options = {}) => {
    return this.post(`/api/v1/private/parent-groups/events/locations`, options,{}, process.env.VUE_APP_BETA_MODE)
  }
}
